import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import Unauthorized from "../components/frontend/auth/Unauthorized";
import Cart from "../components/frontend/cart/Cart";
import PrivacyPolicy from "../components/frontend/policy/PrivacyPolicy";
import ReturnPolicy from "../components/frontend/returnpolicy/ReturnPolicy";
import ShippingPolicy from "../components/frontend/shippingpolicy/ShippingPolicy";
import TermsAndCondition from "../components/frontend/terms/TermsAndCondition";
import {
  AdminPage,
  AnalyticsPage,
  CargoCompaniesPage,
  CargoCompanyUsersPage,
  ExchangeRatePage,
  ItemsManagementPage,
  ItemStatusPage,
  OrderStatusPage,
  OrdersPage,
  PaymentMethodDiscountsPage,
  PricingPage,
  ProductTypeKeywordsPage,
  ProductCachePage,
  ProductShippingMetadataPage,
  PromotionCodesPage,
  RedisCachePage,
  ReportsPage,
  RolesPage,
  TaxesFeesPage,
  UsersPage,
  ReferralSettingsPage,
  ReferralAnalyticsPage,
} from "../pages/backend";
import OrderDetailsPage from "../pages/backend/OrderDetailsPage";
import PromotionCodeAnalyticsPage from "../pages/backend/PromotionCodeAnalyticsPage";
import SalesOverviewPage from "../pages/backend/analytics/SalesOverviewPage";
import CustomerInsightsPage from "../pages/backend/analytics/CustomerInsightsPage";
import ProductPerformancePage from "../pages/backend/analytics/ProductPerformancePage";
import RevenueTimelinePage from "../pages/backend/analytics/RevenueTimelinePage";
import ZincApiUsagePage from "../pages/backend/analytics/ZincApiUsagePage";
import {
  AuthTabs,
  Home,
  NotFoundPage,
  PageLayout,
  VerifyEmail,
} from "../pages/frontend";
import AboutUsPage from "../pages/frontend/AboutUsPage";
import CheckoutPage from "../pages/frontend/CheckoutPage";
import ForgotPassword from "../pages/frontend/ForgotPassword";
import OrderConfirmation from "../pages/frontend/OrderConfirmation";
import ProductDetailsPage from "../pages/frontend/ProductDetails";
import ProfilePage from "../pages/frontend/ProfilePage";
import ResetPassword from "../pages/frontend/ResetPassword";
import SearchPage from "../pages/frontend/Search";
import ReviewPage from "../pages/frontend/ReviewPage";
import CustomerReviewPage from "../pages/frontend/CustomerReviewPage";
import ReviewsPage from "../pages/backend/ReviewsPage";

import ProtectedRoute from "./ProtectedRoute";
import PublicRoute from "./PublicRoute";
import RoleProtectedRoute from "./RoleProtectedRoute";

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Layout Route - This will show navigation on all pages */}
      <Route path="/" element={<PageLayout />}>
        {/* Public Routes */}
        <Route index element={<Home />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="product/:productId" element={<ProductDetailsPage />} />
        <Route path="verify-email" element={<VerifyEmail />} />
        <Route path="about-us" element={<AboutUsPage />} />
        <Route path="terms" element={<TermsAndCondition />} />
        <Route path="return-policy" element={<ReturnPolicy />} />
        <Route path="privacyPolicy" element={<PrivacyPolicy />} />
        <Route path="shipping-policy" element={<ShippingPolicy />} />

        {/* Review Routes */}
        <Route path="review" element={<ReviewPage />} />
        <Route path="review/unsubscribe" element={<ReviewPage />} />
        <Route
          path="review/order/:orderId"
          element={
            <ProtectedRoute>
              <CustomerReviewPage />
            </ProtectedRoute>
          }
        />

        {/* Auth Routes */}
        <Route
          path="auth"
          element={
            <PublicRoute>
              <AuthTabs />
            </PublicRoute>
          }
        />
        <Route
          path="login"
          element={<Navigate to="/auth?tab=login" replace />}
        />
        <Route
          path="register"
          element={<Navigate to="/auth?tab=register" replace />}
        />
        <Route
          path="forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="reset-password"
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />
        <Route path="unauthorized" element={<Unauthorized />} />

        {/* Protected Routes */}
        <Route
          path="profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Cargo Admin Routes */}
        <Route
          path="cargo-admin"
          element={
            <RoleProtectedRoute allowedRoles={["cargo-admin"]}>
              <div>Cargo Panel - To be implemented</div>
            </RoleProtectedRoute>
          }
        />

        {/* Shopper Routes */}
        <Route
          path="shopper"
          element={
            <RoleProtectedRoute allowedRoles={["shopper"]}>
              <div>Shopper Panel - To be implemented</div>
            </RoleProtectedRoute>
          }
        />

        {/* Cart and Checkout Routes */}
        <Route
          path="cart"
          element={
            <ProtectedRoute>
              <Cart />
            </ProtectedRoute>
          }
        />
        <Route
          path="checkout"
          element={
            <ProtectedRoute>
              <CheckoutPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="order-confirmation"
          element={
            <ProtectedRoute>
              <OrderConfirmation />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      {/* Admin Routes */}
      <Route
        path="admin"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <AdminPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/users"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <UsersPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/roles"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <RolesPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/pricing"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <PricingPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/pricing/product-type-keywords"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <ProductTypeKeywordsPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/taxes-fees"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <TaxesFeesPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/promotion-codes"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <PromotionCodesPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/promotion-codes/analytics"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <PromotionCodeAnalyticsPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/referral-settings"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <ReferralSettingsPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/referral-analytics"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <ReferralAnalyticsPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/payment-method-discounts"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <PaymentMethodDiscountsPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/exchange-rates"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <ExchangeRatePage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/orders"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <OrdersPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/orders/:orderNumber"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <OrderDetailsPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/reports"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <ReportsPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/analytics"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <AnalyticsPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/analytics/sales"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <AdminPage><SalesOverviewPage /></AdminPage>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/analytics/customers"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <AdminPage><CustomerInsightsPage /></AdminPage>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/analytics/products"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <AdminPage><ProductPerformancePage /></AdminPage>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/analytics/revenue"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <AdminPage><RevenueTimelinePage /></AdminPage>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/analytics/zinc-usage"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <AdminPage><ZincApiUsagePage /></AdminPage>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/items"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <ItemsManagementPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/order-statuses"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <OrderStatusPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/item-statuses"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <ItemStatusPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/cargo-companies"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <CargoCompaniesPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/cargo-companies/:id/users"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <CargoCompanyUsersPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/product-cache"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <ProductCachePage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/product-shipping-metadata"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <ProductShippingMetadataPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/shipping-metadata"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <ProductShippingMetadataPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/redis-cache"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <RedisCachePage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="admin/reviews"
        element={
          <RoleProtectedRoute allowedRoles={["Admin"]}>
            <ReviewsPage />
          </RoleProtectedRoute>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
